import type { OdometerReadingRow, RecurringTaskRow, RecurringTaskTruckRow, TruckRow, WorkOrderRow } from '@routeback/core';
import type { Repos } from '../../lib/app';
import { blankWorkOrder } from './actions';
import { booking, truckMileage } from './mileage';
import { isOpen } from './workorders';

/** Active trucks a rule covers: all of them, or the ones picked. */
export function trucksFor(task: RecurringTaskRow, members: RecurringTaskTruckRow[], trucks: TruckRow[]): TruckRow[] {
  const live = trucks.filter((t) => !t.deleted_at && t.active);
  if (task.applies_to_all) return live;
  const ids = new Set(members.filter((m) => !m.deleted_at && m.task_id === task.id).map((m) => m.truck_id));
  return live.filter((t) => ids.has(t.id));
}

/** The rule's open scheduled jobs, soonest first. */
export function scheduleFor(task: RecurringTaskRow, wos: WorkOrderRow[]): WorkOrderRow[] {
  return wos.filter((w) => w.recurring_task_id === task.id && isOpen(w)).sort((a, b) => (a.due_on ?? '').localeCompare(b.due_on ?? ''));
}

/** Trucks the rule covers that have no open scheduled job (e.g. a truck added to the fleet later). */
export function unscheduled(task: RecurringTaskRow, members: RecurringTaskTruckRow[], trucks: TruckRow[], wos: WorkOrderRow[]): TruckRow[] {
  const has = new Set(scheduleFor(task, wos).map((w) => w.truck_id));
  return trucksFor(task, members, trucks).filter((t) => !has.has(t.id));
}

export type DueTone = 'overdue' | 'soon' | 'later';
/** Overdue (red), due within 30 days (amber), later. */
export function dueTone(due: string | null, today: string, soonDays = 30): DueTone {
  if (!due) return 'later';
  if (due < today) return 'overdue';
  const t = new Date(`${today}T12:00:00Z`);
  t.setUTCDate(t.getUTCDate() + soonDays);
  return due <= t.toISOString().slice(0, 10) ? 'soon' : 'later';
}

/** "every 90 days" / "every 3 months" / "every year". */
export function everyLabel(days: number | null): string {
  if (!days) return '';
  if (days % 365 === 0) return days === 365 ? 'every year' : `every ${days / 365} years`;
  if (days % 30 === 0 && days >= 60) return `every ${days / 30} months`;
  if (days % 7 === 0 && days < 60) return days === 7 ? 'every week' : `every ${days / 7} weeks`;
  return `every ${days} days`;
}

/** A rule as the form sends it: days and/or miles (whichever comes first); the first booking by date and/or miles from today's estimate. */
export interface RuleDraft {
  name: string; interval_days: number | null; interval_miles: number | null; applies_to_all: boolean; truckIds: string[];
  firstDue: string | null; firstMiles: number | null;
}
export interface FirstDue { date: string | null; miles: number | null; readings: OdometerReadingRow[]; today: string }

/** Book a scheduled job for each truck (skipping trucks that already have one). */
export async function scheduleTrucks(repos: Repos, task: RecurringTaskRow, truckIds: string[], first: FirstDue, wos: WorkOrderRow[]): Promise<number> {
  const has = new Set(scheduleFor(task, wos).map((w) => w.truck_id));
  let n = 0;
  for (const id of truckIds) {
    if (has.has(id)) continue;
    const due = booking(task, truckMileage(first.readings, id), first.today, first.date, first.miles);
    await repos.workOrders.save(blankWorkOrder(repos, { truck_id: id, title: task.name, priority: 'low', ...due, recurring_task_id: task.id }));
    n++;
  }
  return n;
}

export async function createRule(repos: Repos, d: RuleDraft, trucks: TruckRow[], readings: OdometerReadingRow[], today: string): Promise<RecurringTaskRow> {
  const task = await repos.recurringTasks.save(repos.recurringTasks.blank({
    name: d.name.trim(), description: '', interval_days: d.interval_days, interval_miles: d.interval_miles, active: true, applies_to_all: d.applies_to_all,
  }));
  const members: RecurringTaskTruckRow[] = [];
  if (!d.applies_to_all) {
    for (const id of d.truckIds) {
      members.push(await repos.recurringTaskTrucks.save(repos.recurringTaskTrucks.blank({
        task_id: task.id, truck_id: id, last_done_on: null, last_done_miles: null, next_due_on: null, next_due_miles: null,
      })));
    }
  }
  await scheduleTrucks(repos, task, trucksFor(task, members, trucks).map((t) => t.id), { date: d.firstDue, miles: d.firstMiles, readings, today }, []);
  return task;
}

/**
 * Change a rule: rename (open scheduled jobs follow), new interval (applies
 * from the next booking), trucks added (booked for `firstDue`) or removed
 * (their open scheduled job is removed).
 */
export async function updateRule(repos: Repos, task: RecurringTaskRow, d: RuleDraft, members: RecurringTaskTruckRow[], trucks: TruckRow[], wos: WorkOrderRow[], readings: OdometerReadingRow[], today: string): Promise<void> {
  const next = await repos.recurringTasks.save({ ...task, name: d.name.trim(), interval_days: d.interval_days, interval_miles: d.interval_miles, applies_to_all: d.applies_to_all });
  const mine = members.filter((m) => !m.deleted_at && m.task_id === task.id);
  const kept: RecurringTaskTruckRow[] = [];
  if (!d.applies_to_all) {
    for (const m of mine) {
      if (d.truckIds.includes(m.truck_id)) kept.push(m);
      else await repos.recurringTaskTrucks.trash(m.id);
    }
    for (const id of d.truckIds.filter((x) => !mine.some((m) => m.truck_id === x))) {
      kept.push(await repos.recurringTaskTrucks.save(repos.recurringTaskTrucks.blank({
        task_id: task.id, truck_id: id, last_done_on: null, last_done_miles: null, next_due_on: null, next_due_miles: null,
      })));
    }
  } else {
    for (const m of mine) await repos.recurringTaskTrucks.trash(m.id);
  }
  const covered = new Set(trucksFor(next, kept, trucks).map((t) => t.id));
  for (const w of scheduleFor(task, wos)) {
    if (!covered.has(w.truck_id ?? '')) await repos.workOrders.trash(w.id);
    else if (w.title !== next.name) await repos.workOrders.save({ ...w, title: next.name });
  }
  await scheduleTrucks(repos, next, [...covered], { date: d.firstDue, miles: d.firstMiles, readings, today }, wos.filter((w) => covered.has(w.truck_id ?? '')));
}

/** Stop a rule: its open scheduled jobs go too; finished ones stay in the history. */
export async function deleteRule(repos: Repos, task: RecurringTaskRow, wos: WorkOrderRow[]): Promise<void> {
  for (const w of scheduleFor(task, wos)) await repos.workOrders.trash(w.id);
  await repos.recurringTasks.save({ ...task, active: false });
  await repos.recurringTasks.trash(task.id);
}

/** When a truck last had this service: its latest finished job for the rule (or the date carried over from the Driver Portal). */
export function lastDone(task: RecurringTaskRow, truckId: string, wos: WorkOrderRow[], members: RecurringTaskTruckRow[]): { on: string; miles: number | null } | null {
  const done = wos.filter((w) => !w.deleted_at && w.status === 'done' && w.recurring_task_id === task.id && w.truck_id === truckId && w.completed_on)
    .sort((a, b) => (b.completed_on ?? '').localeCompare(a.completed_on ?? ''))[0];
  if (done) return { on: done.completed_on!, miles: done.odometer };
  const m = members.find((x) => !x.deleted_at && x.task_id === task.id && x.truck_id === truckId && x.last_done_on);
  return m ? { on: m.last_done_on!, miles: m.last_done_miles } : null;
}

export interface DueItem { wo: WorkOrderRow; task: RecurringTaskRow; due: string }
/** Sort key for a mileage job whose date can't be estimated yet. */
export const NO_DATE = '9999-12-31';
/** Every open scheduled job, soonest first. */
export function dueList(tasks: RecurringTaskRow[], wos: WorkOrderRow[]): DueItem[] {
  const byId = new Map(tasks.map((t) => [t.id, t]));
  return wos.filter((w) => w.recurring_task_id && byId.has(w.recurring_task_id) && isOpen(w))
    // A mileage job with no date yet (no readings for the truck) sorts last.
    .map((w) => { const task = byId.get(w.recurring_task_id!)!; return { wo: w, task, due: w.due_on ?? (task.interval_miles ? NO_DATE : w.opened_on) }; })
    .sort((a, b) => a.due.localeCompare(b.due) || a.task.name.localeCompare(b.task.name));
}

/** "Counts as" choice when finishing a job that wasn't booked by a rule. */
export type RecurringLink = { kind: 'none' } | { kind: 'task'; taskId: string } | { kind: 'new'; name: string; days: number };

/** A rule whose name matches the job's ("Oil change" ↔ "Oil Change", "Fuel filter" ↔ "Replaced fuel filter"). */
export function guessTask(title: string, tasks: RecurringTaskRow[]): RecurringTaskRow | undefined {
  const t = title.trim().toLowerCase();
  if (!t) return undefined;
  const hits = tasks.filter((x) => { const n = x.name.trim().toLowerCase(); return n && (t === n || t.includes(n) || n.includes(t)); });
  return hits.length === 1 ? hits[0] : undefined;
}

/**
 * Make a finished job count for a recurring service: the truck joins the rule
 * if it wasn't on it, the truck's booked job for it goes (this one replaces
 * it), or a new rule starts for this truck. Returns the rule id to save on the
 * job — finishing it then books the next one from the completion date.
 */
export async function linkToRule(repos: Repos, link: RecurringLink, truckId: string, jobId: string, wos: WorkOrderRow[]): Promise<string | null> {
  if (link.kind === 'none') return null;
  if (link.kind === 'new') {
    const task = await repos.recurringTasks.save(repos.recurringTasks.blank({
      name: link.name.trim(), description: '', interval_days: link.days, interval_miles: null, active: true, applies_to_all: false,
    }));
    await repos.recurringTaskTrucks.save(repos.recurringTaskTrucks.blank({
      task_id: task.id, truck_id: truckId, last_done_on: null, last_done_miles: null, next_due_on: null, next_due_miles: null,
    }));
    return task.id;
  }
  const task = await repos.recurringTasks.get(link.taskId);
  if (!task) return null;
  if (!task.applies_to_all && !(await repos.recurringTaskTrucks.listLive()).some((m) => m.task_id === task.id && m.truck_id === truckId)) {
    await repos.recurringTaskTrucks.save(repos.recurringTaskTrucks.blank({
      task_id: task.id, truck_id: truckId, last_done_on: null, last_done_miles: null, next_due_on: null, next_due_miles: null,
    }));
  }
  for (const w of scheduleFor(task, wos)) if (w.truck_id === truckId && w.id !== jobId) await repos.workOrders.trash(w.id);
  return task.id;
}
